// Dropdown menu following the WAI-ARIA menu button pattern: arrow keys,
// Home/End, Escape and Tab all behave as screen-reader users expect. It
// drops in and fades out (instantly for people who reduce motion).
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router'
import { AnimatePresence, m } from 'framer-motion'
import { cn } from '../../lib/format.js'

export function Menu({ trigger, label, items, header, align = 'end', className, triggerClassName = 'menu-trigger rounded-full' }) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const rootRef = useRef(null)
  const menuId = useId()

  const focusItem = useCallback((index) => {
    const nodes = menuRef.current?.querySelectorAll('[role="menuitem"]')
    if (!nodes?.length) return
    const i = (index + nodes.length) % nodes.length
    nodes[i].focus()
  }, [])

  const close = useCallback((returnFocus = true) => {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return undefined
    focusItem(0)
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open, focusItem])

  const onButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
    }
  }

  const onMenuKeyDown = (event) => {
    const nodes = [...menuRef.current.querySelectorAll('[role="menuitem"]')]
    const current = nodes.indexOf(document.activeElement)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusItem(current + 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      focusItem(current - 1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      focusItem(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      focusItem(nodes.length - 1)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      close()
    } else if (event.key === 'Tab') {
      close(false)
    }
  }

  const itemClass = (danger) =>
    cn(
      'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm outline-none transition-colors duration-150',
      'focus-visible:bg-white/10 hover:bg-white/5',
      danger ? 'text-red-300' : 'text-ink',
    )

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onButtonKeyDown}
        className={triggerClassName}
      >
        {trigger}
      </button>
      <AnimatePresence>
        {open && (
          <m.div
            id={menuId}
            ref={menuRef}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.18, ease: [0.2, 0, 0, 1] } }}
            exit={{ opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.12, ease: [0.4, 0, 1, 1] } }}
            className={cn(
              'absolute top-full z-50 mt-2 w-64 rounded-xl bg-surface-2 p-1.5 shadow-2xl ring-1 shadow-black/50 ring-white/10',
              align === 'end' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
            )}
          >
            {header && <div className="border-b border-hairline px-3 pt-2 pb-3 mb-1.5">{header}</div>}
            {items.map(({ label: itemLabel, icon: Icon, to, href, onSelect, danger }) => {
              const content = (
                <>
                  {Icon && <Icon aria-hidden="true" className="size-4 text-ink-muted" />}
                  {itemLabel}
                </>
              )
              // `href` is a link to another site, opened in a new tab.
              if (href) {
                return (
                  <a key={itemLabel} role="menuitem" tabIndex={-1} href={href} target="_blank" rel="noopener noreferrer nofollow" className={itemClass(danger)} onClick={() => close(false)}>
                    {content}
                  </a>
                )
              }
              return to ? (
                <Link key={itemLabel} role="menuitem" tabIndex={-1} to={to} className={itemClass(danger)} onClick={() => close(false)}>
                  {content}
                </Link>
              ) : (
                <button
                  key={itemLabel}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={itemClass(danger)}
                  onClick={() => {
                    close()
                    onSelect?.()
                  }}
                >
                  {content}
                </button>
              )
            })}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
